import { reactive } from 'vue';
import type { ElementMapOption } from '@/components/DesignStudio/types';

export interface ColorOptionsClip {
    /**
     * 1-indexed, same shape ElementLibrarySelector already renders for any color section. Typed as
     * the wider ElementMapOption (not FontColorOption) only because ShadowColorModel shares that
     * type with the font/image library maps - every value actually stored here is a solid color,
     * gradient or palette.
     */
    colors: Map<number, ElementMapOption>;
    libraryId?: number;
    /** Fill is the only section that can hold gradients/palettes; every other target is solid-only. */
    hasNonSolidColor: boolean;
}

interface ColorOptionsClipboardState {
    clip: ColorOptionsClip | null;
}

/**
 * Module-level singleton, same pattern as useTransformClipboard: every `<vue-component>` mounted by
 * the Angular templates imports this same module, so the clipboard survives across the separate
 * Fill/Stroke/Shadow/Vector panel instances and across element-type selections.
 *
 * Unlike useTransformClipboard this isn't bound to a canvas: a color has no canvas-relative unit, so
 * copying in step 1 and pasting in step 2's preview is fine.
 */
const state = reactive<ColorOptionsClipboardState>({
    clip: null
});

export const useColorOptionsClipboard = () => {
    const copy = (data: Omit<ColorOptionsClip, 'hasNonSolidColor'>) => {
        const hasNonSolidColor = [...data.colors.values()].some(
            (color) => !!color && typeof color === 'object' && ('colorStops' in color || 'colors' in color)
        );
        state.clip = { ...data, hasNonSolidColor };
    };

    const clip = () => state.clip;

    return {
        copy,
        clip
    };
};
